/**
 * Browser-safe rule graph evaluator.
 * Inlined from packages/engine/src/rule-evaluator.ts to avoid pulling in
 * server-only modules (action-handler, configuration-store) that break webpack.
 */

import type { ProductModel } from '@config3d/contracts';

// ── Types (mirrored from engine) ─────────────────────────────────────────────

interface RuleNodeData {
  id: string;
  type: 'attribute' | 'switch' | 'condition' | 'action' | 'logic-and' | 'logic-or' | 'logic-not';
  position: { x: number; y: number };
  data: Record<string, unknown>;
}

interface RuleEdgeData {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
}

export interface SerializedGraph {
  nodes: RuleNodeData[];
  edges: RuleEdgeData[];
}

type ActionType =
  | 'show-object'
  | 'hide-object'
  | 'show-tagged'
  | 'hide-tagged'
  | 'set-visibility'
  | 'set-attribute'
  | 'disable-option'
  | 'enable-option'
  | 'show-component'
  | 'hide-component'
  | 'enable-variant'
  | 'disable-variant'
  | 'apply-material'
  | 'camera-move'
  | 'set-transition'
  | 'enable-modifier'
  | 'disable-modifier'
  | 'set-modifier-param';

export interface RuleAction {
  type: ActionType;
  targetId: string;
  value?: unknown;
}

interface EvaluationContext {
  selections: Record<string, string>;
  attributeOptionKeys?: Record<string, string[]>;
}

interface RuleActionTargetRef {
  kind: 'scene' | 'tag';
  id: string;
}

interface NodeResult {
  value: unknown;
  boolValue?: boolean;
}

// ── Pure evaluation logic ────────────────────────────────────────────────────

function isRuleNodeEnabled(node: RuleNodeData): boolean {
  const e = node.data.enabled;
  if (e === false || e === 'false') return false;
  return true;
}

function evaluateDisabledNodeResult(node: RuleNodeData, inputs: NodeResult[]): NodeResult {
  switch (node.type) {
    case 'attribute':
    case 'switch':
      return { value: '', boolValue: false };
    case 'condition':
    case 'logic-and':
    case 'logic-or':
    case 'logic-not':
      return inputs[0] ?? { value: false, boolValue: false };
    case 'action':
      return { value: null, boolValue: undefined };
    default:
      return { value: null, boolValue: false };
  }
}

function evaluateCondition(value: string, operator: string, compareValue: string): boolean {
  switch (operator) {
    case 'equals': return value === compareValue;
    case 'not-equals': return value !== compareValue;
    case 'contains': return value.includes(compareValue);
    case 'greater-than': return parseFloat(value) > parseFloat(compareValue);
    case 'less-than': return parseFloat(value) < parseFloat(compareValue);
    default: return false;
  }
}

function normalizeConditionCompareList(data: Record<string, unknown>): string[] {
  const raw = data.compareValues;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((x) => String(x)).filter((s) => s.length > 0);
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const p = JSON.parse(raw) as unknown;
      if (Array.isArray(p)) return p.map((x) => String(x)).filter((s) => s.length > 0);
    } catch {
      /* ignore */
    }
  }
  const single = data.compareValue;
  if (single != null && String(single).length > 0) return [String(single)];
  return [];
}

function evaluateConditionAgainstCompares(value: string, operator: string, compares: string[]): boolean {
  if (compares.length === 0) return false;
  if (compares.length === 1) return evaluateCondition(value, operator, compares[0]);
  switch (operator) {
    case 'equals':
      return compares.includes(value);
    case 'not-equals':
      return !compares.includes(value);
    case 'contains':
      return compares.some((c) => value.includes(c));
    case 'greater-than': {
      const v = parseFloat(value);
      return compares.some((c) => v > parseFloat(c));
    }
    case 'less-than': {
      const v = parseFloat(value);
      return compares.some((c) => v < parseFloat(c));
    }
    default:
      return false;
  }
}

function evaluateNode(node: RuleNodeData, inputs: NodeResult[], context: EvaluationContext): NodeResult {
  switch (node.type) {
    case 'attribute':
    case 'switch': {
      const key = node.data.attributeKey as string;
      const value = context.selections[key] ?? '';
      return { value, boolValue: !!value };
    }
    case 'condition': {
      const operator = (node.data.operator as string) || 'equals';
      const compareValue = node.data.compareValue as string;
      const inputValue = inputs[0]?.value ?? '';
      if (operator === 'option-index-equals') {
        const attrKey = node.data.optionIndexAttributeKey as string;
        const keys = context.attributeOptionKeys?.[attrKey];
        const sel = String(
          inputValue !== '' && inputValue !== undefined
            ? inputValue
            : (attrKey ? context.selections[attrKey] ?? '' : ''),
        );
        if (!attrKey || !keys?.length) return { value: false, boolValue: false };
        const idx = keys.indexOf(sel);
        if (idx < 0) return { value: false, boolValue: false };
        const want = parseInt(String(compareValue), 10);
        const match = Number.isFinite(want) && idx + 1 === want;
        return { value: match, boolValue: match };
      }
      const compares = normalizeConditionCompareList(node.data);
      const boolValue = evaluateConditionAgainstCompares(String(inputValue), operator, compares);
      return { value: boolValue, boolValue };
    }
    case 'logic-and': {
      const boolValue = inputs.length > 0 && inputs.every((i) => i.boolValue === true);
      return { value: boolValue, boolValue };
    }
    case 'logic-or': {
      const boolValue = inputs.some((i) => i.boolValue === true);
      return { value: boolValue, boolValue };
    }
    case 'logic-not': {
      const boolValue = inputs.length > 0 ? !inputs[0].boolValue : true;
      return { value: boolValue, boolValue };
    }
    case 'action':
      return { value: null, boolValue: undefined };
    default:
      return { value: null };
  }
}

function parseDisabledSwitchOptions(data: Record<string, unknown> | undefined): Set<string> {
  if (!data) return new Set();
  const raw = data.disabledOptions;
  if (Array.isArray(raw)) return new Set(raw.map(String).filter(Boolean));
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) return new Set(p.map(String).filter(Boolean));
    } catch {
      return new Set();
    }
  }
  return new Set();
}

function isSwitchOptionEnabled(node: RuleNodeData, optionKey: string): boolean {
  if (node.type !== 'switch') return true;
  return !parseDisabledSwitchOptions(node.data).has(optionKey);
}

function shouldExecuteAction(
  nodeId: string,
  incomingEdges: Map<string, RuleEdgeData[]>,
  nodeResults: Map<string, NodeResult>,
  nodeMap: Map<string, RuleNodeData>,
): boolean {
  const edges = incomingEdges.get(nodeId) || [];
  if (edges.length === 0) return true;
  return edges.some((edge) => {
    const src = nodeResults.get(edge.source);
    if (!src) return false;
    if (edge.sourceHandle === 'true') return src.boolValue === true;
    if (edge.sourceHandle === 'false') return src.boolValue === false;
    if (edge.sourceHandle) {
      if (String(src.value) !== edge.sourceHandle) return false;
      const srcNode = nodeMap.get(edge.source);
      if (srcNode && !isSwitchOptionEnabled(srcNode, edge.sourceHandle)) return false;
      return true;
    }
    return src.boolValue !== false;
  });
}

function filterTargetRefArray(raw: unknown[]): RuleActionTargetRef[] {
  return raw.filter((x): x is RuleActionTargetRef => {
    if (x == null || typeof x !== 'object') return false;
    const k = (x as RuleActionTargetRef).kind;
    const id = (x as RuleActionTargetRef).id;
    return (k === 'scene' || k === 'tag') && typeof id === 'string' && id.length > 0;
  });
}

function parseActionTargetsField(data: Record<string, unknown>, field: string): RuleActionTargetRef[] {
  const raw = data[field];
  if (Array.isArray(raw)) return filterTargetRefArray(raw);
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const p = JSON.parse(raw) as unknown;
      if (Array.isArray(p)) return filterTargetRefArray(p);
    } catch {
      return [];
    }
  }
  return [];
}

function parseActionTargets(data: Record<string, unknown>): RuleActionTargetRef[] {
  return parseActionTargetsField(data, 'actionTargets');
}

function parseModifierRuleParamValue(raw: unknown): string | number | boolean {
  if (typeof raw === 'boolean' || typeof raw === 'number') return raw;
  if (raw == null) return '';
  const s = String(raw).trim();
  if (s.toLowerCase() === 'true') return true;
  if (s.toLowerCase() === 'false') return false;
  if (s === '') return '';
  const n = Number(s);
  if (!Number.isNaN(n) && Number.isFinite(n) && String(n) === s) return n;
  const f = parseFloat(s);
  if (!Number.isNaN(f) && Number.isFinite(f) && String(f) === s) return f;
  return s;
}

function parseModifierKeyList(data: Record<string, unknown>, field: string): string[] {
  const raw = data[field];
  let arr: unknown[] = [];
  if (Array.isArray(raw)) arr = raw;
  else if (typeof raw === 'string' && raw.trim()) {
    try {
      const p = JSON.parse(raw) as unknown;
      if (Array.isArray(p)) arr = p;
      else if (!raw.trim().startsWith('[')) return [raw.trim()];
    } catch {
      if (raw.trim()) return [raw.trim()];
    }
  }
  const out: string[] = [];
  const seen = new Set<string>();
  for (const x of arr) {
    const s = String(x ?? '').trim();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

function buildActions(node: RuleNodeData): RuleAction[] {
  const actionType = node.data.actionType as ActionType;
  if (!actionType) return [];

  if (actionType === 'set-visibility') {
    const show = parseActionTargetsField(node.data, 'visibilityShowTargets');
    const hide = parseActionTargetsField(node.data, 'visibilityHideTargets');
    if (show.length === 0 && hide.length === 0) return [];
    return [{ type: 'set-visibility', targetId: '', value: { show, hide } }];
  }

  const targets = parseActionTargets(node.data);
  const legacyTarget = (node.data.targetId as string) || '';
  const value = node.data.value;

  const effective: RuleActionTargetRef[] =
    targets.length > 0
      ? targets
      : legacyTarget
        ? [{ kind: 'scene' as const, id: legacyTarget }]
        : [];

  const out: RuleAction[] = [];

  if (actionType === 'show-tagged' || actionType === 'hide-tagged') {
    if (targets.length > 0) {
      const tagOnly = targets.filter((t) => t.kind === 'tag');
      if (tagOnly.length > 0) return tagOnly.map((t) => ({ type: actionType, targetId: t.id }));
    }
    if (!legacyTarget) return [];
    return [{ type: actionType, targetId: legacyTarget }];
  }

  if (actionType === 'show-object' || actionType === 'hide-object') {
    if (effective.length === 0) return [];
    for (const t of effective) {
      if (t.kind === 'tag') {
        out.push({ type: actionType === 'show-object' ? 'show-tagged' : 'hide-tagged', targetId: t.id });
      } else {
        out.push({ type: actionType, targetId: t.id });
      }
    }
    return out;
  }

  if (actionType === 'apply-material') {
    if (value == null || String(value) === '') return [];
    if (effective.length === 0) return [];
    for (const t of effective) {
      out.push({ type: 'apply-material', targetId: t.kind === 'tag' ? `tag:${t.id}` : t.id, value });
    }
    return out;
  }

  if (actionType === 'enable-modifier' || actionType === 'disable-modifier') {
    if (effective.length === 0) return [];
    const enableKeys = parseModifierKeyList(node.data, 'modifierEnableKeys');
    const disableKeys = parseModifierKeyList(node.data, 'modifierDisableKeys');
    const legacy = String(value ?? '').trim();
    // Legacy single-value nodes (before enable/disable checklists).
    if (enableKeys.length === 0 && disableKeys.length === 0 && legacy) {
      if (actionType === 'enable-modifier') enableKeys.push(legacy);
      else disableKeys.push(legacy);
    }
    // Emit enables first, then disables so disable wins for the same key.
    for (const modKey of enableKeys) {
      for (const t of effective) {
        out.push({
          type: 'enable-modifier',
          targetId: t.kind === 'tag' ? `tag:${t.id}` : t.id,
          value: modKey,
        });
      }
    }
    for (const modKey of disableKeys) {
      for (const t of effective) {
        out.push({
          type: 'disable-modifier',
          targetId: t.kind === 'tag' ? `tag:${t.id}` : t.id,
          value: modKey,
        });
      }
    }
    return out;
  }

  if (actionType === 'set-modifier-param') {
    const modKey = String(value ?? '').trim();
    const paramKey = String(node.data.modifierParamKey ?? '').trim();
    if (!modKey || !paramKey || effective.length === 0) return [];
    const paramValue = parseModifierRuleParamValue(node.data.modifierParamValue);
    const payload = { modifierKey: modKey, paramKey, paramValue };
    for (const t of effective) {
      out.push({
        type: 'set-modifier-param',
        targetId: t.kind === 'tag' ? `tag:${t.id}` : t.id,
        value: payload,
      });
    }
    return out;
  }

  if (!legacyTarget) return [];
  return [{ type: actionType, targetId: legacyTarget, value }];
}

function getInputValues(nodeId: string, incomingEdges: Map<string, RuleEdgeData[]>, nodeResults: Map<string, NodeResult>): NodeResult[] {
  const edges = incomingEdges.get(nodeId) || [];
  return edges.map((e) => nodeResults.get(e.source)).filter((r): r is NodeResult => r !== undefined);
}

function topologicalSort(nodes: RuleNodeData[], edges: RuleEdgeData[]): string[] {
  const inDegree = new Map<string, number>();
  const adjList = new Map<string, string[]>();
  for (const node of nodes) { inDegree.set(node.id, 0); adjList.set(node.id, []); }
  for (const edge of edges) { adjList.get(edge.source)?.push(edge.target); inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1); }
  const queue: string[] = [];
  for (const [id, deg] of inDegree) { if (deg === 0) queue.push(id); }
  const result: string[] = [];
  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    result.push(nodeId);
    for (const neighbor of adjList.get(nodeId) || []) {
      const newDeg = (inDegree.get(neighbor) || 1) - 1;
      inDegree.set(neighbor, newDeg);
      if (newDeg === 0) queue.push(neighbor);
    }
  }
  return result;
}

function evaluateRuleGraph(graph: SerializedGraph, context: EvaluationContext): { actions: RuleAction[] } {
  const actions: RuleAction[] = [];
  const nodeMap = new Map<string, RuleNodeData>();
  const incomingEdges = new Map<string, RuleEdgeData[]>();
  const nodeResults = new Map<string, NodeResult>();

  for (const node of graph.nodes) nodeMap.set(node.id, node);
  for (const edge of graph.edges) {
    if (!incomingEdges.has(edge.target)) incomingEdges.set(edge.target, []);
    incomingEdges.get(edge.target)!.push(edge);
  }

  const sorted = topologicalSort(graph.nodes, graph.edges);

  for (const nodeId of sorted) {
    const node = nodeMap.get(nodeId);
    if (!node) continue;
    if ((node.type as string) === 'option-group') continue;
    const enabled = isRuleNodeEnabled(node);
    const inputs = getInputValues(nodeId, incomingEdges, nodeResults);
    const result = enabled ? evaluateNode(node, inputs, context) : evaluateDisabledNodeResult(node, inputs);
    nodeResults.set(nodeId, result);

    if (node.type === 'action' && enabled && shouldExecuteAction(nodeId, incomingEdges, nodeResults, nodeMap)) {
      for (const a of buildActions(node)) actions.push(a);
    }
  }

  return { actions };
}

// ── Public API ───────────────────────────────────────────────────────────────

function selectionsToStringRecord(selections: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(selections)) {
    if (typeof v === 'string') out[k] = v;
    else if (v != null) out[k] = String(v);
  }
  return out;
}

function productModelToOptionKeys(productModel: ProductModel | null): Record<string, string[]> {
  if (!productModel) return {};
  const out: Record<string, string[]> = {};
  for (const a of productModel.attributes) {
    const opts = a.values || [];
    out[a.key] = opts
      .map((o: { key?: string; id?: string }) => String(o?.key ?? o?.id ?? ''))
      .filter((key: string) => key.length > 0);
  }
  return out;
}

export function evaluateRuleGraphsForSelections(
  graphs: SerializedGraph[],
  selections: Record<string, unknown>,
  productModel: ProductModel | null,
): RuleAction[] | null {
  if (graphs.length === 0) return null;
  const sel = selectionsToStringRecord(selections);
  const attributeOptionKeys = productModelToOptionKeys(productModel);
  const actions: RuleAction[] = [];
  try {
    for (const graph of graphs) {
      if (!graph?.nodes?.length) continue;
      const result = evaluateRuleGraph(graph, { selections: sel, attributeOptionKeys });
      actions.push(...result.actions);
    }
  } catch (err) {
    console.warn('[C3D Rules] Client-side evaluation error:', err);
    // null = caller must keep prior actions (empty [] would un-hide every mesh for a frame).
    return null;
  }
  return actions;
}
